/**
 * Seeded deterministic RNG. The whole match is reproducible from
 * (seed, decks, actions), so the generator state must live inside GameState.
 *
 * mulberry32 is a 32-bit state PRNG: fast, no dependencies, good enough for
 * shuffles and random target selection.
 */

/** Advance the state and return a float in [0, 1) plus the next state. */
export function mulberry32(state: number): { value: number; next: number } {
  let a = (state + 0x6d2b79f5) | 0;
  const nextState = a;
  a = Math.imul(a ^ (a >>> 15), a | 1);
  a ^= a + Math.imul(a ^ (a >>> 7), a | 61);
  const value = ((a ^ (a >>> 14)) >>> 0) / 4294967296;
  return { value, next: nextState >>> 0 };
}

/** Normalize an arbitrary number or string seed into a uint32. */
export function seedToState(seed: number | string): number {
  if (typeof seed === 'number') return seed >>> 0;
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** A tiny mutable cursor over the RNG state, so callers can draw several values. */
export class Rng {
  state: number;

  constructor(state: number) {
    this.state = state >>> 0;
  }

  /** Float in [0, 1). */
  next(): number {
    const { value, next } = mulberry32(this.state);
    this.state = next;
    return value;
  }

  /** Integer in [0, max). Returns 0 when max <= 0. */
  int(max: number): number {
    if (max <= 0) return 0;
    return Math.floor(this.next() * max) % max;
  }

  /** Fisher-Yates in place, using this cursor. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }
    return items;
  }

  /** Pick n distinct items deterministically. */
  pick<T>(items: readonly T[], n: number): T[] {
    const copy = items.slice();
    this.shuffle(copy);
    return copy.slice(0, Math.max(0, Math.min(n, copy.length)));
  }
}
